/**
 * configSchema.js — THE single source of truth for governed config taxonomies.
 *
 * This module is imported by BOTH runtimes:
 *   - the Azure Functions API  → ./configValidation.js, ../functions/putConfig.js
 *     (raw Node ESM, no bundler) via a plain relative `./configSchema.js`.
 *   - the Vite frontend         → src/lib/configRegistry.js via the `@shared`
 *     alias configured in vite.config.js (rollup bundles it into the SPA).
 *
 * It lives inside api/src/lib (not a sibling `shared/` dir) on purpose: the
 * Azure Functions deploy package is built from the `api/` folder, so anything
 * outside `api/` would be dropped from the deployed function. Keeping it here
 * guarantees the server always ships the rules it validates against.
 *
 * HARD RULE: this file must stay runtime-agnostic — NO imports of `mssql`,
 * `@azure/*`, React, Vite, `fs`, `path`, or any browser/Node-only global.
 * Pure data + pure functions only, so both bundlers can consume it untouched.
 *
 * Adding a new governed config type = add ONE entry to CONFIG_SCHEMA below.
 * Both the server validator and the client registry pick it up automatically.
 */

import {
  buildCodeSet,
  buildTreatmentSet,
  cellKey,
  cellLabel,
  diffCategoryLists,
  enumerateCells,
  getCellCategories,
  resolveSubtypes,
  migrateSubtypeDeclarations,
} from './incidentCategoryCascade.js';
import {
  validateNotificationRules,
  validateCaseSubmittedNotification,
  validateTreatmentSubmitNotify,
  validateRecipientGroups,
  validateScheduledNotificationsEnabled,
  isStaffEmail,
  STAFF_EMAIL_DOMAINS,
} from './notificationSchema.js';
import { validateRoleDefinitions, diffRoleDefinitions } from './roleSchema.js';

// ── Badge css allowlist — mirrors the .badge.* swatches in styles/global.css ─────
// Empty string ('') = the default/neutral badge, always allowed.
export const BADGE_CSS = Object.freeze([
  '', 'sev-1', 'sev-2', 'sev-3', 'sev-4', 'sev-5',
  'status-new', 'status-open', 'status-inv', 'status-wait', 'status-res', 'status-esc',
  'tx-lhr', 'tx-awi',
]);

const BADGE_CSS_SET = new Set(BADGE_CSS);

export const isAllowedBadge = (v) => BADGE_CSS_SET.has(v ?? '');

// ── Small shared predicates ──────────────────────────────────────────────────────
export const isBlank = (v) => v == null || String(v).trim() === '';
const norm = (v) => String(v ?? '').trim().toLowerCase();

// ── Field-level validators keyed by field "type" ────────────────────────────────
// Each returns an error string or null. Used by the server (authoritative) AND
// the client mirror so a rule can only ever be written once.
export const FIELD_VALIDATORS = {
  label: (val) =>
    isBlank(val) ? 'Label cannot be empty.'
    : String(val).length > 120 ? 'Label is too long (max 120).'
    : null,
  value: (val) =>
    isBlank(val) ? 'Value cannot be empty.'
    : String(val).length > 120 ? 'Value is too long (max 120).'
    : null,
  text: () => null,
  order: (val) => {
    if (val === '' || val == null) return 'Order is required.';
    const n = Number(val);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Order must be a whole number.';
    if (n < 0 || n > 9999) return 'Order must be between 0 and 9999.';
    return null;
  },
  colour: (val) => (isAllowedBadge(val) ? null : `Unknown badge style "${val}".`),
  bool: () => null,
  aliases: (val) => {
    if (val == null) return null;
    if (!Array.isArray(val)) return 'Aliases must be a list.';
    return null;
  },
  eventTypes: (val) => {
    if (val == null) return null;
    if (!Array.isArray(val)) return 'Event-type availability must be a list.';
    return null;
  },
  treatmentTypes: (val) => {
    if (val == null) return null;
    if (!Array.isArray(val)) return 'Treatment-type availability must be a list.';
    return null;
  },
  subtypes: (val) => {
    if (val == null) return null;
    if (!Array.isArray(val)) return 'Subtypes must be a list.';
    const seen = new Set();
    for (const s of val) {
      if (isBlank(s)) return 'Subtype names cannot be empty.';
      if (String(s).length > 120) return 'Subtype name is too long (max 120).';
      const k = norm(s);
      if (seen.has(k)) return `Duplicate subtype "${s}".`;
      seen.add(k);
    }
    return null;
  },
};

// ── List-level invariants keyed by rule name ─────────────────────────────────────
export const LIST_RULES = {
  nonEmpty: (items, schema) =>
    items.length === 0 ? `At least one ${schema.label.toLowerCase()} is required.` : null,

  noDuplicateLabel: (items) => {
    const seen = new Set();
    for (const it of items) {
      const k = norm(it.label);
      if (!k) continue;
      if (seen.has(k)) return `Duplicate label "${it.label}".`;
      seen.add(k);
    }
    return null;
  },

  noDuplicateValue: (items) => {
    const seen = new Set();
    for (const it of items) {
      const k = norm(it.value);
      if (!k) continue;
      if (seen.has(k)) return `Duplicate value "${it.value}".`;
      seen.add(k);
    }
    return null;
  },

  atLeastOneActive: (items) =>
    items.some(it => it.isActive) ? null : 'At least one status must be active.',
};

// ── The governed taxonomies ──────────────────────────────────────────────────────
// idField   — the unique identity field for a row.
// fields    — declarative specs; `type` selects a FIELD_VALIDATOR (server) AND an
//             editor control (client, via configRegistry.js).
// rules     — list-level invariants (LIST_RULES keys).
export const CONFIG_SCHEMA = {
  severityCodes: {
    label: 'Severity code',
    idField: 'key',
    fields: [
      { name: 'label',       type: 'label'  },
      { name: 'value',       type: 'value'  },
      { name: 'description', type: 'text'   },
      { name: 'cssClass',    type: 'colour' },
      { name: 'order',       type: 'order'  },
      { name: 'eventTypes',  type: 'eventTypes' },
    ],
    rules: ['noDuplicateLabel', 'noDuplicateValue', 'nonEmpty'],
  },
  statusValues: {
    label: 'Status value',
    idField: 'key',
    fields: [
      { name: 'label',    type: 'label'  },
      { name: 'value',    type: 'value'  },
      { name: 'cssClass', type: 'colour' },
      { name: 'isActive', type: 'bool'   },
      { name: 'order',    type: 'order'  },
      { name: 'eventTypes', type: 'eventTypes' },
      { name: 'treatmentTypes', type: 'treatmentTypes' },
    ],
    rules: ['noDuplicateLabel', 'noDuplicateValue', 'nonEmpty', 'atLeastOneActive'],
  },
  treatmentTypes: {
    label: 'Treatment type',
    idField: 'key',
    fields: [
      { name: 'label',       type: 'label'   },
      { name: 'value',       type: 'value'   },
      { name: 'description', type: 'text'    },
      { name: 'aliases',     type: 'aliases' },
      { name: 'subtypes',    type: 'subtypes' },
      { name: 'cssClass',    type: 'colour'  },
      { name: 'order',       type: 'order'   },
      { name: 'eventTypes',  type: 'eventTypes' },
    ],
    rules: ['noDuplicateLabel', 'noDuplicateValue', 'nonEmpty'],
  },
  // Per-case "requirement" types — the picker on the case Notes panel. A row is
  // just { key, label }; the case stores the resolved label, and the server only
  // ever trusts a key that exists in this list (or the 'other' free-text path).
  requirementTypes: {
    label: 'Requirement type',
    idField: 'key',
    fields: [
      { name: 'label', type: 'label' },
    ],
    rules: ['noDuplicateLabel', 'nonEmpty'],
  },
};

export const INCIDENT_CATEGORIES_KEY = 'incidentCategoriesByTreatmentAndCode';
export const NOTIFICATION_RULES_KEY = 'notificationRules';
export const CASE_SUBMITTED_NOTIFICATION_KEY = 'caseSubmittedNotification';
export const TREATMENT_SUBMIT_NOTIFY_KEY = 'treatmentSubmitNotify';
export const RECIPIENT_GROUPS_KEY = 'recipientGroups';
export const SCHEDULED_NOTIFICATIONS_ENABLED_KEY = 'scheduledNotificationsEnabled';
export const ROLE_DEFINITIONS_KEY = 'roleDefinitions';
export const CLINIC_ACCESS_GRANTS_KEY = 'clinicAccessGrants';

// ── Code 4/5 response SLA (sla_escalation_logic.md v2, build contract §10) ─────
export const SLA_ENABLED_KEY                 = 'slaEnabled';
export const SLA_HOURS_PER_LEG_KEY           = 'slaHoursPerLeg';      // Codes 4–5 (the short turn)
export const SLA_HOURS_PER_LEG_LOW_KEY       = 'slaHoursPerLegLow';   // Codes 1–3 (the long turn)
export const SLA_HQ_CALENDAR_KEY             = 'slaHqCalendar';
export const SLA_CLINIC_DEFAULT_CALENDAR_KEY = 'slaClinicDefaultCalendar';
export const SLA_SIDES_KEY                   = 'slaSides';
export const SLA_CONFIG_KEYS = Object.freeze([
  SLA_ENABLED_KEY, SLA_HOURS_PER_LEG_KEY, SLA_HOURS_PER_LEG_LOW_KEY, SLA_HQ_CALENDAR_KEY, SLA_CLINIC_DEFAULT_CALENDAR_KEY, SLA_SIDES_KEY,
]);

const GOVERNED_KEYS = new Set([
  ...Object.keys(CONFIG_SCHEMA),
  INCIDENT_CATEGORIES_KEY,
  NOTIFICATION_RULES_KEY,
  CASE_SUBMITTED_NOTIFICATION_KEY,
  TREATMENT_SUBMIT_NOTIFY_KEY,
  RECIPIENT_GROUPS_KEY,
  SCHEDULED_NOTIFICATIONS_ENABLED_KEY,
  ROLE_DEFINITIONS_KEY,
  CLINIC_ACCESS_GRANTS_KEY,
  ...SLA_CONFIG_KEYS,
]);

export const isGovernedKey = (key) => GOVERNED_KEYS.has(key);

const MAX_CATEGORY_LEN = 120;

function validateCategoryList(list, cellPath, errors, byCell) {
  const push = (msg) => {
    (byCell[cellPath] ||= []).push(msg);
    errors.push(msg);
  };
  if (!Array.isArray(list)) {
    push(`${cellPath}: categories must be a list.`);
    return;
  }
  const seen = new Set();
  for (const raw of list) {
    const s = String(raw ?? '').trim();
    if (!s) {
      push(`${cellPath}: category cannot be empty.`);
      continue;
    }
    if (s.length > MAX_CATEGORY_LEN) {
      push(`${cellPath}: category "${s.slice(0, 20)}…" is too long (max ${MAX_CATEGORY_LEN}).`);
    }
    const k = norm(s);
    if (seen.has(k)) {
      push(`${cellPath}: duplicate category "${s}".`);
    }
    seen.add(k);
  }
}

function validateTreatmentNode(treatmentType, node, codeSet, errors, byCell) {
  const treatment = treatmentType.value;
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    errors.push(`${treatment}: must be an object.`);
    return;
  }
  const keys = Object.keys(node);
  if (keys.length === 0) return;

  // Whether a treatment is nested is decided SOLELY by its DECLARED subtypes
  // (the first-class `subtypes` field). Legacy rows are migrated to a declared
  // field before we get here, so there is no tree-shape guessing — validation
  // and the runtime read share this one source of truth. The root fix for the
  // validation hole: in a flat (no-subtype) treatment an unknown key is an
  // unknown SEVERITY CODE — never a silently-accepted subtype.
  const subtypeSet = new Set(resolveSubtypes(treatmentType));
  const nested = subtypeSet.size > 0;

  if (!nested) {
    for (const code of keys) {
      if (!codeSet.has(code)) {
        errors.push(`${treatment}: unknown severity code "${code}".`);
        continue;
      }
      validateCategoryList(node[code], cellKey(treatment, null, code), errors, byCell);
    }
    return;
  }

  // Nested: every key must be a declared subtype. A severity-code key here is a
  // structural error (a code sitting where a subtype belongs); an undeclared key
  // is an unknown subtype. The published tree always matches the declared model —
  // the editor never publishes an orphan branch (remove-with-keep stashes the
  // branch in editor state rather than leaving it undeclared in the tree).
  for (const key of keys) {
    if (codeSet.has(key)) {
      errors.push(`${treatment}: cannot mix subtype keys and severity-code keys at the same level.`);
      continue;
    }
    if (!subtypeSet.has(key)) {
      errors.push(`${treatment}: unknown subtype "${key}".`);
      continue;
    }
    const sub = node[key];
    if (!sub || typeof sub !== 'object' || Array.isArray(sub)) {
      errors.push(`${treatment} · ${key}: must be an object keyed by severity code.`);
      continue;
    }
    for (const code of Object.keys(sub)) {
      if (!codeSet.has(code)) {
        errors.push(`${treatment} · ${key}: unknown severity code "${code}".`);
        continue;
      }
      validateCategoryList(sub[code], cellKey(treatment, key, code), errors, byCell);
    }
  }
}

function validateIncidentCategories(value, context = {}) {
  const errors = [];
  const byCell = {};
  const severityCodes = context.severityCodes || [];
  // Default legacy rows so `subtypes` is always declared — then validation reads
  // ONLY the declared field (no inference at the read). One source of truth.
  const treatmentTypes = migrateSubtypeDeclarations(context.treatmentTypes || [], value, severityCodes);
  const allowedTreatments = buildTreatmentSet(treatmentTypes);
  const codeSet = buildCodeSet(severityCodes);

  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: ['Incident categories must be an object tree.'], byCell, byId: byCell };
  }

  for (const key of Object.keys(value)) {
    if (!allowedTreatments.has(key)) {
      errors.push(`Unknown treatment "${key}".`);
    }
  }

  // Iterate the treatment ROWS (not just values) so each node is validated
  // against that treatment's declared subtype set.
  for (const tx of treatmentTypes) {
    if (!tx?.value || !allowedTreatments.has(tx.value)) continue;
    const node = value[tx.value];
    if (node === undefined) continue;
    validateTreatmentNode(tx, node, codeSet, errors, byCell);
  }

  return {
    ok: errors.length === 0,
    errors: [...new Set(errors)],
    byCell,
    byId: byCell,
  };
}

function diffIncidentCategories(oldValue, newValue, context = {}) {
  const treatmentTypes = context.treatmentTypes || [];
  const severityCodes = context.severityCodes || [];
  const codeSet = buildCodeSet(severityCodes);

  const oldCells = enumerateCells(oldValue || {}, treatmentTypes, severityCodes);
  const newCells = enumerateCells(newValue || {}, treatmentTypes, severityCodes);
  const allPaths = new Map();

  for (const c of oldCells) allPaths.set(c.path, c);
  for (const c of newCells) allPaths.set(c.path, c);

  const changed = [];
  for (const [path, meta] of allPaths) {
    const oldList = getCellCategories(oldValue, meta.treatment, meta.subtype, meta.code, codeSet);
    const newList = getCellCategories(newValue, meta.treatment, meta.subtype, meta.code, codeSet);
    const d = diffCategoryLists(oldList, newList);
    if (d.changed) {
      changed.push({
        path,
        label: meta.label || cellLabel(meta.treatment, meta.subtype, meta.code),
        treatment: meta.treatment,
        subtype: meta.subtype,
        code: meta.code,
        added: d.added,
        removed: d.removed,
        reordered: d.reordered,
        from: oldList,
        to: newList,
      });
    }
  }

  return { cells: { changed } };
}

// ── clinicAccessGrants (config key) ──────────────────────────────────────────
// A grant WIDENS one user's clinic access, additively + temporarily:
//   { id, email, clinics: string[], expiresAt: <ISO>, grantedBy, grantedAt, note? }
// Rules enforced here (server-authoritative, mirrored on the client):
//   - email must clear the staff-domain floor (isStaffEmail),
//   - clinics a NON-EMPTY array of non-blank strings (grants are clinics-only),
//   - expiresAt a valid ISO date string (a PAST date is allowed — expiry is
//     enforced at read time, and edits may touch already-expired rows),
//   - unique ids across the array, array capped at MAX_CLINIC_ACCESS_GRANTS.
// SuperAdmin-only write rides the governed-key path in canWriteConfigKey; there
// is nothing role-specific to check here.
export const MAX_CLINIC_ACCESS_GRANTS = 500;
const MAX_GRANT_CLINICS = 200;
const MAX_GRANT_NOTE_LEN = 300;
// Grants are TEMPORARY — cap how far out an expiry can be set so a grant can't
// be made effectively permanent. Past dates stay valid (an expired grant is a
// harmless historical row); only the upper bound is enforced.
export const MAX_GRANT_DAYS = 90;

/** A parseable ISO date string (not just any Date.parse-able value). */
function isIsoDateString(v) {
  if (typeof v !== 'string' || !v.trim()) return false;
  const t = Date.parse(v);
  return Number.isFinite(t);
}

/** Validate the whole `clinicAccessGrants` array. Returns { ok, errors, byId }. */
export function validateClinicAccessGrants(value) {
  const errors = [];
  const byId = {};
  if (!Array.isArray(value)) {
    return { ok: false, errors: ['clinicAccessGrants must be an array.'], byId };
  }
  if (value.length > MAX_CLINIC_ACCESS_GRANTS) {
    errors.push(`Too many grants (max ${MAX_CLINIC_ACCESS_GRANTS}).`);
  }
  const seenIds = new Set();
  value.forEach((g, i) => {
    const idRaw = g && typeof g === 'object' ? g.id : undefined;
    const id = idRaw != null ? String(idRaw) : `(row ${i + 1})`;
    const push = (msg) => { (byId[id] ||= []).push(msg); errors.push(`${id}: ${msg}`); };

    if (!g || typeof g !== 'object' || Array.isArray(g)) {
      errors.push(`Row ${i + 1} is not a valid object.`);
      return;
    }
    if (isBlank(g.id)) push('needs an id.');
    else if (seenIds.has(String(g.id))) push(`duplicate id "${g.id}".`);
    if (!isBlank(g.id)) seenIds.add(String(g.id));

    if (!isStaffEmail(g.email)) push(`email must be a staff address (${STAFF_EMAIL_DOMAINS.join(' / ')}).`);

    if (!Array.isArray(g.clinics) || g.clinics.length === 0) {
      push('needs at least one clinic.');
    } else {
      if (g.clinics.length > MAX_GRANT_CLINICS) push(`too many clinics (max ${MAX_GRANT_CLINICS}).`);
      if (g.clinics.some(c => typeof c !== 'string' || !c.trim())) push('clinic names must be non-empty strings.');
    }

    if (!isIsoDateString(g.expiresAt)) push('expiresAt must be a valid ISO date.');
    else if (Date.parse(g.expiresAt) > Date.now() + MAX_GRANT_DAYS * 86400000) {
      push(`expiresAt cannot be more than ${MAX_GRANT_DAYS} days from now.`);
    }

    if (g.note != null && (typeof g.note !== 'string' || g.note.length > MAX_GRANT_NOTE_LEN)) {
      push(`note must be a string (max ${MAX_GRANT_NOTE_LEN}).`);
    }
  });
  return { ok: errors.length === 0, errors: [...new Set(errors)], byId };
}

// ── SLA config validators ─────────────────────────────────────────────────────
// A calendar = { week: { mon..sun: [["HH:MM","HH:MM"], ...] | [] }, closedDates:
// ["YYYY-MM-DD"], tzByOps?: { IOI, GB, US: <IANA zone> } }. The time zone is NOT
// part of the calendar itself — the HQ calendar carries tzByOps (the case's ops
// picks the zone) and clinic legs take the same zone. Empty day = closed.
// lib/workingHours.js consumes exactly this shape and tolerates junk; the
// validators here stop junk from being published in the first place.

export const SLA_WEEKDAYS = Object.freeze(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export const SLA_OPS_GROUPS = Object.freeze(['IOI', 'GB', 'US']);
export const SLA_HOURS_PER_LEG_MIN = 1;
export const SLA_HOURS_PER_LEG_MAX = 48;
const SLA_MAX_CLOSED_DATES = 400;
const SLA_MAX_INTERVALS_PER_DAY = 4;
const SLA_MAX_SIDE_ROLES = 50;

/** Code defaults — the SPA's DEFAULT_CONFIG and the API's fallbacks both read these. */
export const SLA_DEFAULTS = Object.freeze({
  [SLA_ENABLED_KEY]: false,                       // kill switch — nothing runs until an admin flips it
  [SLA_HOURS_PER_LEG_KEY]: 6,        // Codes 4–5
  [SLA_HOURS_PER_LEG_LOW_KEY]: 24,   // Codes 1–3 (product-owner decision 2026-09-11)
  [SLA_HQ_CALENDAR_KEY]: Object.freeze({
    week: Object.freeze({
      mon: [['09:00', '17:30']], tue: [['09:00', '17:30']], wed: [['09:00', '17:30']],
      thu: [['09:00', '17:30']], fri: [['09:00', '17:30']], sat: [], sun: [],
    }),
    closedDates: Object.freeze([]),
    tzByOps: Object.freeze({ IOI: 'Europe/Dublin', GB: 'Europe/London', US: 'America/New_York' }),
  }),
  // Clinics are usually closed on Monday only (spec Q14): Tue–Sun 09:00–18:00.
  [SLA_CLINIC_DEFAULT_CALENDAR_KEY]: Object.freeze({
    week: Object.freeze({
      mon: [], tue: [['09:00', '18:00']], wed: [['09:00', '18:00']], thu: [['09:00', '18:00']],
      fri: [['09:00', '18:00']], sat: [['09:00', '18:00']], sun: [['09:00', '18:00']],
    }),
    closedDates: Object.freeze([]),
  }),
  // Role NAMES as resolved by auth.js (built-in values / custom role values).
  // Custom roles scoped to a countryGroup (the Country Managers) count as HQ
  // automatically (slaEngine.sideForCaller) even when not listed here.
  [SLA_SIDES_KEY]: Object.freeze({
    hq:     Object.freeze(['SuperAdmin', 'Supervisor', 'Admin', 'HoRO', 'DirectorIOI', 'DirectorGB']),
    clinic: Object.freeze(['ClinicManager', 'ClinicMailbox']),
  }),
});

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const YMD  = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "HH:MM" → minutes since midnight, or null when malformed. */
export function hhmmToMinutes(v) {
  const m = HHMM.exec(String(v ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** True for a real calendar date written YYYY-MM-DD. */
export function isYmdDate(v) {
  const m = YMD.exec(String(v ?? '').trim());
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const back = new Date(Date.UTC(y, mo - 1, d));
  return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d;
}

/** True when Intl accepts the string as an IANA time zone (both runtimes ship Intl). */
export function isIanaTimeZone(tz) {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate one working-hours calendar. `requireTz` (HQ) demands a tzByOps map
 * covering every ops group; the clinic default calendar carries no zone.
 * @returns {{ ok: boolean, errors: string[], byId: Record<string,string[]> }}
 */
export function validateSlaCalendar(value, { requireTz = false, label = 'Calendar' } = {}) {
  const errors = [];
  const byId = {};
  const push = (id, msg) => { (byId[id] ||= []).push(msg); errors.push(`${label}: ${msg}`); };

  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: [`${label} must be an object { week, closedDates }.`], byId };
  }
  const week = value.week;
  if (week == null || typeof week !== 'object' || Array.isArray(week)) {
    push('week', 'week must be an object keyed mon..sun.');
  } else {
    for (const k of Object.keys(week)) {
      if (!SLA_WEEKDAYS.includes(k)) push('week', `unknown weekday "${k}".`);
    }
    let anyOpen = false;
    for (const day of SLA_WEEKDAYS) {
      const intervals = week[day];
      if (intervals == null) continue;                 // absent = closed
      if (!Array.isArray(intervals)) { push(day, `${day}: must be a list of [open, close] pairs.`); continue; }
      if (intervals.length > SLA_MAX_INTERVALS_PER_DAY) push(day, `${day}: too many intervals (max ${SLA_MAX_INTERVALS_PER_DAY}).`);
      let lastClose = -1;
      for (const iv of intervals) {
        if (!Array.isArray(iv) || iv.length !== 2) { push(day, `${day}: each interval is ["HH:MM","HH:MM"].`); continue; }
        const open = hhmmToMinutes(iv[0]);
        const close = hhmmToMinutes(iv[1]);
        if (open == null || close == null) { push(day, `${day}: times must be HH:MM (24h).`); continue; }
        if (close <= open) { push(day, `${day}: closing time must be after opening time.`); continue; }
        if (open < lastClose) push(day, `${day}: intervals overlap or are out of order.`);
        lastClose = close;
        anyOpen = true;
      }
    }
    if (!anyOpen) push('week', 'at least one day must have opening hours.');
  }

  const closed = value.closedDates;
  if (closed != null) {
    if (!Array.isArray(closed)) push('closedDates', 'closedDates must be a list of YYYY-MM-DD dates.');
    else {
      if (closed.length > SLA_MAX_CLOSED_DATES) push('closedDates', `too many closed dates (max ${SLA_MAX_CLOSED_DATES}).`);
      const seen = new Set();
      for (const d of closed) {
        if (!isYmdDate(d)) { push('closedDates', `"${String(d).slice(0, 20)}" is not a YYYY-MM-DD date.`); continue; }
        if (seen.has(d)) push('closedDates', `duplicate closed date ${d}.`);
        seen.add(d);
      }
    }
  }

  const tzByOps = value.tzByOps;
  if (requireTz) {
    if (tzByOps == null || typeof tzByOps !== 'object' || Array.isArray(tzByOps)) {
      push('tzByOps', 'tzByOps must map IOI / GB / US to IANA time zones.');
    } else {
      for (const ops of SLA_OPS_GROUPS) {
        if (!isIanaTimeZone(tzByOps[ops])) push('tzByOps', `tzByOps.${ops} must be an IANA time zone (e.g. Europe/Dublin).`);
      }
      for (const k of Object.keys(tzByOps)) {
        if (!SLA_OPS_GROUPS.includes(k)) push('tzByOps', `unknown ops group "${k}" in tzByOps.`);
      }
    }
  } else if (tzByOps != null) {
    push('tzByOps', 'this calendar takes no tzByOps (the HQ zone applies).');
  }

  for (const k of Object.keys(value)) {
    if (!['week', 'closedDates', 'tzByOps'].includes(k)) push(k, `unknown field "${k}".`);
  }
  return { ok: errors.length === 0, errors: [...new Set(errors)], byId };
}

/** slaSides = { hq: [roleName], clinic: [roleName] } — non-empty, disjoint. */
export function validateSlaSides(value) {
  const errors = [];
  const byId = {};
  const push = (id, msg) => { (byId[id] ||= []).push(msg); errors.push(msg); };
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: ['slaSides must be an object { hq: [...], clinic: [...] }.'], byId };
  }
  const seen = new Map();
  for (const side of ['hq', 'clinic']) {
    const list = value[side];
    if (!Array.isArray(list) || list.length === 0) { push(side, `${side} must be a non-empty list of role names.`); continue; }
    if (list.length > SLA_MAX_SIDE_ROLES) push(side, `${side}: too many roles (max ${SLA_MAX_SIDE_ROLES}).`);
    for (const r of list) {
      if (typeof r !== 'string' || !r.trim()) { push(side, `${side}: role names must be non-empty strings.`); continue; }
      if (r.length > 120) push(side, `${side}: role name "${r.slice(0, 20)}…" is too long (max 120).`);
      const k = r.trim().toLowerCase();
      if (seen.has(k)) push(side, `"${r}" is listed on both sides.`);
      seen.set(k, side);
    }
  }
  for (const k of Object.keys(value)) {
    if (!['hq', 'clinic'].includes(k)) push(k, `unknown side "${k}" (only hq and clinic).`);
  }
  return { ok: errors.length === 0, errors: [...new Set(errors)], byId };
}

export function validateSlaEnabled(value) {
  return typeof value === 'boolean'
    ? { ok: true, errors: [], byId: {} }
    : { ok: false, errors: ['slaEnabled must be true or false.'], byId: {} };
}

export function validateSlaHoursPerLeg(value, key = SLA_HOURS_PER_LEG_KEY) {
  if (!Number.isInteger(value) || value < SLA_HOURS_PER_LEG_MIN || value > SLA_HOURS_PER_LEG_MAX) {
    return { ok: false, errors: [`${key} must be a whole number between ${SLA_HOURS_PER_LEG_MIN} and ${SLA_HOURS_PER_LEG_MAX}.`], byId: {} };
  }
  return { ok: true, errors: [], byId: {} };
}

/**
 * Validate a config value against its schema. AUTHORITATIVE shape used by the
 * server; the client calls the same function for instant feedback.
 * @returns {{ ok: boolean, errors: string[], byId: Record<string,string[]> }}
 *   Unknown keys are treated as ok so non-governed config keys keep working.
 */
export function validateConfigValue(key, value, context = {}) {
  if (key === INCIDENT_CATEGORIES_KEY) {
    return validateIncidentCategories(value, context);
  }
  if (key === NOTIFICATION_RULES_KEY) {
    return validateNotificationRules(value);
  }
  if (key === CASE_SUBMITTED_NOTIFICATION_KEY) {
    return validateCaseSubmittedNotification(value);
  }
  if (key === TREATMENT_SUBMIT_NOTIFY_KEY) {
    return validateTreatmentSubmitNotify(value);
  }
  if (key === RECIPIENT_GROUPS_KEY) {
    return validateRecipientGroups(value);
  }
  if (key === SCHEDULED_NOTIFICATIONS_ENABLED_KEY) {
    return validateScheduledNotificationsEnabled(value);
  }
  if (key === ROLE_DEFINITIONS_KEY) {
    return validateRoleDefinitions(value);
  }
  if (key === CLINIC_ACCESS_GRANTS_KEY) {
    return validateClinicAccessGrants(value);
  }
  if (key === SLA_ENABLED_KEY)                 return validateSlaEnabled(value);
  if (key === SLA_HOURS_PER_LEG_KEY)           return validateSlaHoursPerLeg(value);
  if (key === SLA_HOURS_PER_LEG_LOW_KEY)       return validateSlaHoursPerLeg(value, SLA_HOURS_PER_LEG_LOW_KEY);
  if (key === SLA_HQ_CALENDAR_KEY)             return validateSlaCalendar(value, { requireTz: true, label: 'HQ calendar' });
  if (key === SLA_CLINIC_DEFAULT_CALENDAR_KEY) return validateSlaCalendar(value, { requireTz: false, label: 'Clinic default calendar' });
  if (key === SLA_SIDES_KEY)                   return validateSlaSides(value);

  const schema = CONFIG_SCHEMA[key];
  if (!schema) return { ok: true, errors: [], byId: {} };

  const errors = [];
  const byId = {};
  const pushRow = (id, msg) => {
    if (id != null) (byId[id] ||= []).push(msg);
    errors.push(msg);
  };

  if (!Array.isArray(value)) {
    return { ok: false, errors: [`${schema.label} config must be a list.`], byId };
  }

  // Per-row, per-field validation.
  value.forEach((item, idx) => {
    if (item == null || typeof item !== 'object') {
      errors.push(`Row ${idx + 1} is not a valid object.`);
      return;
    }
    const id = item[schema.idField];
    if (isBlank(id)) {
      errors.push(`Row ${idx + 1} is missing its "${schema.idField}".`);
    }
    for (const field of schema.fields) {
      const fn = FIELD_VALIDATORS[field.type];
      if (!fn) continue;
      const msg = fn(item[field.name], item);
      if (msg) pushRow(id, msg);
    }
  });

  // Duplicate idField check.
  const ids = new Set();
  for (const item of value) {
    const id = item?.[schema.idField];
    if (id == null) continue;
    if (ids.has(id)) pushRow(id, `Duplicate ${schema.idField} "${id}".`);
    ids.add(id);
  }

  // List-level invariants.
  for (const rule of schema.rules || []) {
    const fn = LIST_RULES[rule];
    if (!fn) continue;
    const msg = fn(value, schema);
    if (msg) errors.push(msg);
  }

  return { ok: errors.length === 0, errors: [...new Set(errors)], byId };
}

/**
 * Compute a compact old→new diff for the audit trail and the publish preview.
 * Returns { added, removed, changed } keyed by idField, or a scalar fallback for
 * non-list config values. Both server (audit) and client (preview) use this.
 */
export function diffConfig(key, oldValue, newValue, context = {}) {
  if (key === INCIDENT_CATEGORIES_KEY) {
    return diffIncidentCategories(oldValue, newValue, context);
  }
  if (key === ROLE_DEFINITIONS_KEY) {
    return diffRoleDefinitions(oldValue, newValue);
  }

  const schema = CONFIG_SCHEMA[key];
  if (!schema || !Array.isArray(oldValue) || !Array.isArray(newValue)) {
    return { scalar: { from: oldValue ?? null, to: newValue ?? null } };
  }
  const id = schema.idField;
  const oldById = new Map(oldValue.map(i => [i[id], i]));
  const newById = new Map(newValue.map(i => [i[id], i]));

  const added = [], removed = [], changed = [];
  for (const [k, v] of newById) {
    if (!oldById.has(k)) added.push({ id: k, label: v.label ?? v.value ?? k });
  }
  for (const [k, v] of oldById) {
    if (!newById.has(k)) removed.push({ id: k, label: v.label ?? v.value ?? k });
  }
  for (const [k, oldItem] of oldById) {
    const newItem = newById.get(k);
    if (!newItem) continue;
    const fieldDiffs = {};
    for (const field of schema.fields) {
      const a = JSON.stringify(oldItem[field.name] ?? null);
      const b = JSON.stringify(newItem[field.name] ?? null);
      if (a !== b) fieldDiffs[field.name] = { from: oldItem[field.name] ?? null, to: newItem[field.name] ?? null };
    }
    if (Object.keys(fieldDiffs).length) {
      changed.push({ id: k, label: newItem.label ?? newItem.value ?? k, fields: fieldDiffs });
    }
  }
  return { added, removed, changed };
}
